import { Link } from 'react-router-dom';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import RoleLinkCardGrid from '../app/components/RoleLinkCardGrid';
import type { IconType } from 'react-icons';
import {
  LuArrowRight,
  LuBadgeCheck,
  LuBrain,
  LuBuilding2,
  LuCalculator,
  LuCircleCheck,
  LuCpu,
  LuHeadphones,
  LuHeartPulse,
  LuLandmark,
  LuLaptop,
  LuMessageSquare,
  LuSettings,
  LuShieldCheck,
  LuStar,
  LuTimer,
  LuTrendingUp,
  LuUser,
  LuUserCheck,
  LuUsers,
  LuZap,
} from 'react-icons/lu';

import AdvantageTilesGrid from '../app/components/AdvantageTilesGrid';
import HeroCtaActions from '../app/components/HeroCtaActions';
import GradientCTA from '../app/components/GradientCTA';
import HomeHeroFeatureCards from '../app/components/HomeHeroFeatureCards';
import HomeProcessFlow from '../app/components/HomeProcessFlow';
import ImpactResultsSection from '../app/components/ImpactResultsSection';
import IndustriesWeServeBand from '../app/components/IndustriesWeServeBand';
import RevealSection from '../app/components/RevealSection';
import SbrIcon from '../app/components/SbrIcon';
import TrustedLogoMarquee from '../app/components/TrustedLogoMarquee';
import { SBR_IMPACT_STATS } from '../data/sbrImpactStats';

const PERSON_IMG = `${process.env.PUBLIC_URL}/images/homepage_person.JPG`;

const heroFeatures: {
  title: string;
  body: string;
  Icon: IconType;
}[] = [
  {
    title: 'AI Precision Candidate Discovery',
    body: 'Surfacing the highest-fit candidates from across the market.',
    Icon: LuBrain,
  },
  {
    title: 'Human Expertise',
    body: 'Domain experts validate, qualify, and engage the best candidates.',
    Icon: LuUser,
  },
  {
    title: 'Exclusive to you',
    body: "We deliver unique shortlists you won't find anywhere else.",
    Icon: LuBadgeCheck,
  },
];

const advantages = [
  {
    id: 'niche-roles-filled',
    stat: '95%+',
    title: 'Technical/Niche Roles Filled',
    body: 'Specialist and hard-to-fill searches delivered with precision.',
    Icon: LuCpu,
  },
  {
    id: 'average-time-to-hire',
    stat: '47 Days',
    title: 'Average Time-to-Hire',
    body: 'Move from open role to signed offer in weeks, not months.',
    Icon: LuTimer,
  },
  {
    id: 'stick-rate',
    stat: '96%',
    title: 'Stick Rate',
    body: 'Hires who stay, perform, and grow with your team.',
    Icon: LuStar,
  },
  {
    id: 'exclusive-shortlists',
    stat: '100%',
    title: 'Exclusive Shortlists',
    body: 'Every candidate we present is exclusively yours.',
    Icon: LuShieldCheck,
  },
];

const processSteps = [
  { title: 'Understand', body: 'We align on your goals, role requirements, and success profile.', Icon: LuMessageSquare },
  { title: 'AI-Powered Search', body: 'Our AI scans millions of data points to build the strongest candidate pool.', Icon: LuBrain },
  { title: 'Human Validation', body: 'Domain experts validate, qualify, and engage the best-fit candidates.', Icon: LuUserCheck },
  { title: 'Exclusive Shortlist', body: 'You receive a curated, exclusive shortlist of top-tier talent.', Icon: LuBadgeCheck },
];

const roles = [
  { title: 'Finance & Accounting', Icon: LuCalculator },
  { title: 'IT & Technology', Icon: LuLaptop },
  { title: 'Operations & Supply Chain', Icon: LuSettings },
  { title: 'Sales & Revenue', Icon: LuTrendingUp },
  { title: 'Customer Experience', Icon: LuHeadphones },
  { title: 'HR & People Ops', Icon: LuUsers },
];

const industries = [
  { title: 'Energy & Utilities', Icon: LuZap },
  { title: 'Manufacturing & Industrial', Icon: LuBuilding2 },
  { title: 'Information Technology & Software', Icon: LuCpu },
  { title: 'Healthcare & Life Sciences', Icon: LuHeartPulse },
  { title: 'Financial Services', Icon: LuLandmark },
];

export default function HomePage() {
  return (
    <>
      {/* Hero */}
      <section className="home-hero text-white">
        <div className="home-hero__main">
          <div className="home-hero__media">
            <img
              src={PERSON_IMG}
              alt="Professional holding a tablet"
              className="home-hero__img"
              width={1920}
              height={1080}
              decoding="async"
              fetchPriority="high"
            />
            <div className="home-hero__fade" aria-hidden />
            <div className="home-hero__media-overlay d-lg-none">
              <div className="home-hero__media-cta">
                <HeroCtaActions block="home-hero" />
              </div>
            </div>
          </div>
          <Container className="home-hero__content">
            <Row className="home-hero__row gy-4">
              <Col lg={6} xl={6} className="home-hero__copy-col">
                <div className="home-hero__copy">
                  <div className="home-hero__headline">
                    <h1 className="display-5 fw-bold mb-0 pb-1 lh-sm">
                      <span className="home-hero__headline-line">Hire critical talent.</span>
                      <span className="home-hero__headline-line home-hero__word-cost">Quality candidates.</span>
                      <span className="home-hero__headline-line home-hero__word-faster">Faster.</span>
                    </h1>
                  </div>
                  <div className="home-hero__body">
                    <p className="lead text-white-50 mb-4 pe-lg-3">
                      SBR2TH combines AI-powered precision with human expertise to deliver high-caliber talent for your
                      hardest-to-fill roles.
                    </p>
                    <div className="home-hero__actions d-none d-lg-flex flex-column flex-sm-row gap-3 mb-4">
                      <HeroCtaActions block="home-hero" />
                    </div>
                    <Row className="g-3 home-hero__checks">
                      {[
                        '95%+ Niche Roles Filled',
                        '47-Day Average Time-to-Hire',
                        '96% Stick Rate',
                        'Exclusive Shortlists',
                      ].map((label) => (
                        <Col xs={6} key={label}>
                          <div className="d-flex gap-2 align-items-center small">
                            <SbrIcon icon={LuCircleCheck} className="text-sbr-teal flex-shrink-0" size={18} />
                            <span className="text-white-50">{label}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
          <HomeHeroFeatureCards items={heroFeatures} />
        </div>

        <div className="home-trusted text-white">
          <Container className="home-trusted__inner">
            <Row className="align-items-center gy-3">
              <Col lg={3} xl={3} className="home-trusted__label-col">
                <p className="home-trusted__label text-sbr-blue mb-0">Trusted by companies across industries</p>
              </Col>
              <Col lg={9} xl={9}>
                <TrustedLogoMarquee />
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      {/* The SBR2TH Advantage */}
      <section className="bg-white py-5 py-lg-6">
          <Container>
            <RevealSection>
              <div className="sbr-section-header text-center mb-5">
                <h2 className="sbr-section__title fw-bold mb-3">The SBR2TH Advantage</h2>
                <p className="sbr-section__lead home-section-lead mb-0 mx-auto">
                  A better hiring experience backed by technology and human insight.
                </p>
              </div>
            </RevealSection>
            <div className="mb-5">
              <AdvantageTilesGrid items={advantages} colMd={6} colXl={3} />
            </div>
            <Card className="home-advantage-banner border-0 bg-sbr-navy text-white">
              <Card.Body className="p-4 p-lg-5 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">
                <div className="d-flex align-items-center gap-3 gap-lg-4 flex-grow-1">
                  <div className="home-advantage-banner__icon flex-shrink-0" aria-hidden>
                    <SbrIcon icon={LuUsers} size={26} />
                  </div>
                  <div className="home-advantage-banner__copy text-white-50 mb-0">
                    <p className="mb-2">
                      We specialize in high-impact, hard-to-fill roles across industries.
                    </p>
                    <p className="mb-0">
                      From Finance and Technology to Operations and Customer Experience.
                    </p>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  className="home-advantage-banner__btn flex-shrink-0 fw-semibold"
                  as={Link as any}
                  to="/roles-we-fill"
                >
                  Explore Roles We Fill <SbrIcon icon={LuArrowRight} className="ms-2" size={16} />
                </Button>
              </Card.Body>
            </Card>
          </Container>
      </section>

      {/* How We Work */}
      <section className="bg-white py-5 py-lg-6 border-top border-secondary border-opacity-10">
          <Container>
            <RevealSection>
              <div className="sbr-section-header text-center mb-5">
                <h2 className="sbr-section__title fw-bold mb-3">How We Work</h2>
                <p className="sbr-section__lead home-section-lead mb-0 mx-auto">
                  A proven process. Smarter technology. Better outcomes.
                </p>
              </div>
            </RevealSection>
            <HomeProcessFlow steps={processSteps} />
          </Container>
      </section>

      {/* Roles We Fill */}
      <section className="bg-sbr-navy text-white py-5 py-lg-6">
          <Container>
            <div className="sbr-section-header text-center mb-5">
              <h2 className="sbr-section__title fw-bold mb-3">Roles We Fill</h2>
              <p className="sbr-section__lead sbr-section__lead--on-dark home-section-lead home-section-lead--on-dark mb-0 mx-auto">
                Specialized in critical, high-impact, hard-to-fill roles.
              </p>
            </div>
            <RoleLinkCardGrid
              className="mb-4"
              items={roles}
              to="/roles-we-fill"
              variant="dark"
              layout="stacked"
              columns={6}
            />
            <div className="text-center">
              <Button variant="link" className="text-sbr-teal text-decoration-none fw-semibold" as={Link as any} to="/roles-we-fill">
                View All Roles We Fill <SbrIcon icon={LuArrowRight} className="ms-1" size={18} />
              </Button>
            </div>
          </Container>
      </section>

      <IndustriesWeServeBand
        items={industries}
        subtitle=""
        layout="grid"
        cardLayout="stacked"
        columns={5}
      />

      <ImpactResultsSection
        eyebrow="Results That Matter"
        title={
          <>
            Real impact for <span className="text-gradient-teal-blue">growing teams.</span>
          </>
        }
        lead="We help companies hire smarter, move faster, and build stronger teams."
        cta={{ to: '/book-a-call', label: 'Book a 30 Min Call' }}
        stats={SBR_IMPACT_STATS}
      />

      <GradientCTA
        icon={<i className="bi bi-calendar-event" aria-hidden />}
        title={
          <>
            <div>Ready to experience a better way to hire?</div>
            <div className="sbr-cta__subtitle fw-normal opacity-75 mt-2">Let&apos;s build your next great team.</div>
          </>
        }
      />
    </>
  );
}
